import axios from 'axios';
import { store } from '../store';
import queryClient from '../queryClient';
import { harvestMockIds, urlCarriesMockId } from './idHelper';

// tourInterceptor — short-circuits axios GETs with canned mock responses
// while a JoyRide tour is active on a "hub-style" screen.
//
// Detail screens (CultivationBatch, BatchActivity, etc.) drive their tour
// data through the reducer via useTour → SET_RECORD dispatches. That works
// because each screen owns one record. Hub screens (CultivationHub,
// ScheduledActivities, ProductionHub, …) instead render an aggregation of
// widgets, each fetching its own data via React Query / axios. Wiring
// SET_RECORD-style mock dispatches into every widget would be invasive;
// intercepting at the network layer is a single shared mechanism.
//
// === How it works ===
// On every outgoing axios request, the interceptor checks
// `state.help.activeTourScreen` (published by guideHelper.useGuide while a
// tour runs). If set, it looks up that screen's mock registry; if the
// outgoing URL matches an entry, it swaps in a no-network adapter that
// resolves immediately with the canned data. Widgets receive the mock
// response as if it came from the server.
//
// === v1 scope ===
// - GETs only. Writes (POST/PUT/DELETE) pass through. Tours are awareness-
//   oriented; spotlightClicks discourages mutations during the tour.
// - One mock dataset per screen — hub tours are non-lifecycle. Detail
//   tours that need per-step lifecycle data continue using the reducer
//   path; the interceptor leaves them alone (no detail-screen URLs are
//   registered here).
// - String-includes matching by default; pass a RegExp pattern for finer
//   control. Order matters — first match wins.
//
// === Second job: blocking mock ids ===
// This is also where requests carrying a training mock id get dropped. Detail
// tours swap a simulated record into the reducer, and the shared widgets
// (Comments, Attachments, ObjectLog, Signature, …) build record-scoped URLs
// out of that record's _id — a readable string, not an ObjectId, so the server
// answers with a CastError 500 and logs the whole stack. Each of those widgets
// checks isPersistedId() at its own call site; this is the net under the ones
// nobody has audited yet, keyed on the mock-id registry in idHelper.js.
//
// Unlike the mock-response path above, the net is NOT gated on a tour being
// active. The fetch that prompted this work fires as the tour tears down —
// clicking Attachments or Comments lands outside Joyride's spotlight, which
// ends the tour — so a tour-active gate would race it and lose.

// Module-level registry. Populated by screen training files via
// registerTourMocks(screenId, { '<urlPattern>': (config) => mockData, ... }).
// Lives outside React so it's available to the axios interceptor without
// any provider context.
const mockRegistry = {};

// Public — call once at module load from a screen's training file
// (e.g. sopui/src/training/cultivationHub.js). Subsequent calls for the
// same screenId overwrite.
export function registerTourMocks(screenId, mocks) {
    if (!screenId || !mocks) return;
    mockRegistry[screenId] = mocks;
}

function findMock(config) {
    // Only intercept GETs in v1. Writes/deletes go through to the real API.
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get') return null;

    const state = store.getState();
    const screenId = state?.help?.activeTourScreen;
    if (!screenId) return null;

    const screenMocks = mockRegistry[screenId];
    if (!screenMocks) return null;

    const url = config.url || '';
    for (const [pattern, handler] of Object.entries(screenMocks)) {
        const matches = pattern instanceof RegExp
            ? pattern.test(url)
            : url.includes(pattern);
        if (matches) return handler(config);
    }
    return null;
}

// Answer a request locally. axios calls config.adapter to make the network
// call, so replacing it is the supported way to resolve without a socket.
function resolveLocally(config, data) {
    config.adapter = () => Promise.resolve({
        data,
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
        request: {}
    });
    return config;
}

// Blocked requests REJECT rather than resolve, for every method.
//
// Resolving looked friendlier and was wrong. These endpoints don't share a
// response shape — /api/batchledgers/:id returns a bare array, /api/comment/
// list/:id returns { data, totalCount }, /api/schedule/get/:id returns a
// record — so no single canned body fits, and handing back the wrong one turns
// a silent no-op into a render crash (BatchLedgers does setList(res.data) then
// list.map, which threw on an object).
//
// Rejecting reproduces exactly what happened before this net existed: the
// request went out, the server threw a CastError, the promise rejected, and
// the `await` never reached the setter — so state stayed at its initial value
// and nothing rendered wrong. Same control flow now, minus the round-trip and
// the 500 in the log.
//
// The error is shaped as an axios cancellation so the `if (!axios.isCancel(err))`
// guards these call sites already have stay quiet, rather than showing the user
// a failure they didn't cause.
function rejectLocally(config, url) {
    config.adapter = () => {
        const err = new Error(`Blocked ${url}: training mock id`);
        err.__CANCEL__ = true;              // what axios.isCancel() reads
        err.code = 'ERR_CANCELED';
        err.config = config;
        return Promise.reject(err);
    };
    return config;
}

axios.interceptors.request.use((config) => {
    const mockData = findMock(config);
    if (mockData === null || mockData === undefined) {
        // No registered mock for this URL — but it may still carry a training
        // mock id, which the server can only answer with a CastError 500.
        const url = config.url || '';
        if (urlCarriesMockId(url)) {
            const method = (config.method || 'get').toUpperCase();
            console.debug(`[tour] blocked ${method} ${url} — training mock id`);
            return rejectLocally(config, url);
        }
        return config;
    }

    // Hub mocks carry their own fake row ids, which the widgets then link to.
    // Harvest them off the canned body so a click-through on a mocked row is
    // covered by the same net.
    harvestMockIds(mockData);

    // Short-circuit the request — no network traffic, and the response
    // interceptors still run normally on the mock body.
    return resolveLocally(config, mockData);
});

// Subscribe to redux activeTourScreen changes. Whenever it transitions
// (null → screenId, screenId → null, or screenId → otherScreenId), wipe
// React Query's cache so widgets refetch fresh — mock data on tour start,
// real data on tour end. This is broad-strokes invalidation; for v1 the
// blast radius is fine since tours are infrequent. Tighten with predicate
// matching later if cache thrashing becomes a concern.
let lastActiveScreen = null;
store.subscribe(() => {
    const current = store.getState()?.help?.activeTourScreen || null;
    if (current === lastActiveScreen) return;
    lastActiveScreen = current;
    // invalidateQueries({}) refetches every active query.
    queryClient.invalidateQueries();
});
